/**
 * TopBar — global top command bar.
 *
 * Left:   crimson logo mark + "KANTERA" wordmark + "Soccer Intelligence" tagline
 * Center: full-text search input (pill, ⌘K shortcut, navigates to /search)
 * Right:  club pill (switch org / jump to a team) → profile-view switcher
 *         (Kantera Admin / Coach / Parent) → bell → user identity cluster
 *
 * Wired to real auth data via useAuth(). No stubs. Law 9.
 *
 * Org-switching: calls POST /api/auth/switch-org via switchOrg() in AuthContext.
 * If the user has >1 org, the pill opens a popover listing all orgs (real data
 * from GET /api/auth/my-orgs). Single-org users get a static display pill (no
 * dropdown, no chevron). "Switching…" transient state is exposed from AuthContext.
 */

import React, { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  css,
  flexRow,
  flexCol,
  alpha,
  buttonReset,
  useToast,
  useTranslationTyped,
} from '@kantera/ui';
import { theme } from '@kantera/ui';
import { useAuth } from '../hooks/useAuth';
import { useResponsive } from '../hooks/useResponsive';
import { useUserProfile } from '../context/UserProfileContext';
import { resolveUserDisplayName, resolveUserInitials } from '../utils/userDisplayName';
import { switchOrgFailureMessage } from '../utils/switchOrgError';
import { UserMenu } from './UserMenu';
import { useApi } from '../hooks/useApi';
import { useProfileViewContext } from '../context/ProfileViewContext';
import type { OrgMembership, TeamListResponse } from '@kantera/types';

// ---------------------------------------------------------------------------
// Role label mapping — covers the full Role union from @kantera/types
// ---------------------------------------------------------------------------

function formatRoleLabel(role: string | undefined): string {
  if (!role) return '';
  const roleMap: Record<string, string> = {
    owner: 'Owner',
    admin: 'Club Admin',
    coach: 'Head Coach',
    assistant_coach: 'Assistant Coach',
    team_manager: 'Team Manager',
    parent: 'Parent',
    player: 'Player',
    referee: 'Referee',
    staff: 'Club Staff',
    platform_admin: 'Kantera Admin',
    founder: 'Founder',
  };
  return roleMap[role] ?? role.charAt(0).toUpperCase() + role.slice(1);
}

// ---------------------------------------------------------------------------
// Initials helper
// ---------------------------------------------------------------------------

function getInitials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  if (!first) return '?';
  return parts.length === 1
    ? first.charAt(0).toUpperCase()
    : (first.charAt(0) + last.charAt(0)).toUpperCase();
}

/**
 * Deterministically pick an accent hue from the org name so each org
 * consistently gets its own color without needing a stored value.
 * Mirrors the same palette used in OrgSwitcher.tsx.
 */
function orgAccentColor(displayName: string): string {
  const PALETTE = [
    theme.colors.accent.primary,
    '#34d399',
    '#f59e0b',
    '#f472b6',
    '#60a5fa',
    '#a78bfa',
    '#fb923c',
  ];
  let hash = 0;
  for (let i = 0; i < displayName.length; i++) {
    hash = (hash * 31 + displayName.charCodeAt(i)) >>> 0;
  }
  return PALETTE[hash % PALETTE.length] ?? (PALETTE[0] as string);
}

// ---------------------------------------------------------------------------
// Inline SVG icons — 16×16 / 20×20 viewBox, currentColor stroke
// ---------------------------------------------------------------------------

const IconSearch = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M16.5 16.5l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconBell = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.73 21a2 2 0 0 1-3.46 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconChevronDown = ({ rotated = false }: { rotated?: boolean }) => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{
      flexShrink: 0,
      transform: rotated ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.18s ease',
    }}
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 6L9 17l-5-5"
      stroke={theme.colors.accent.primary}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Kantera logomark — ported 1:1 from the v2 mockup (kanteraMarkSVG): a
// Telstar soccer ball with a red/white particle trail + ambient brand glow.
// Recolors with the brand token via currentColor-style var refs.
const KanteraMark = () => {
  const brand = 'var(--k-brand)';
  return (
    <svg
      viewBox="0 0 80 64"
      width="40"
      height="32"
      fill="none"
      aria-hidden="true"
      style={{
        flexShrink: 0,
        filter:
          'drop-shadow(0 0 8px rgba(230,57,70,0.45)) drop-shadow(0 0 18px rgba(230,57,70,0.22))',
      }}
    >
      <defs>
        <radialGradient id="km-ball" cx="0.35" cy="0.30" r="0.85">
          <stop offset="0%" stopColor="#2a2a30" />
          <stop offset="45%" stopColor="#16171a" />
          <stop offset="85%" stopColor="#0a0a0c" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>
        <radialGradient id="km-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#e63946" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#e63946" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="km-redfade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#e63946" stopOpacity="0" />
          <stop offset="100%" stopColor="#e63946" stopOpacity="1" />
        </linearGradient>
        <linearGradient id="km-whitefade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.9" />
        </linearGradient>
      </defs>
      {/* ambient brand glow halo */}
      <circle cx="56" cy="32" r="28" fill="url(#km-glow)" />
      {/* particle trail — alternating red and white shards */}
      <g>
        <polygon points="2,32 6,30 6,34" fill="url(#km-redfade)" opacity="0.30" />
        <polygon points="5,26 9,24 9,28" fill="url(#km-whitefade)" opacity="0.40" />
        <polygon points="5,38 9,36 9,40" fill="url(#km-redfade)" opacity="0.45" />
        <polygon points="9,21 14,19 14,23" fill="url(#km-redfade)" opacity="0.55" />
        <polygon points="9,42 14,41 14,45" fill="url(#km-whitefade)" opacity="0.55" />
        <polygon points="13,28 18,26 17,32" fill="url(#km-redfade)" opacity="0.70" />
        <polygon points="13,36 18,34 17,40" fill="url(#km-whitefade)" opacity="0.60" />
        <polygon points="17,24 22,22 22,26" fill="url(#km-whitefade)" opacity="0.75" />
        <polygon points="17,40 22,38 22,42" fill="url(#km-redfade)" opacity="0.75" />
        <polygon points="21,31 26,29 25,35" fill="url(#km-redfade)" opacity="0.85" />
        <polygon points="25,28 30,26 30,30" fill="url(#km-whitefade)" opacity="0.85" />
        <polygon points="25,36 30,34 30,38" fill="url(#km-redfade)" opacity="0.90" />
        <polygon points="29,32 34,30 33,36" fill="url(#km-whitefade)" opacity="0.90" />
        <polygon points="33,30 38,28 37,35" fill="url(#km-redfade)" opacity="0.98" />
      </g>
      {/* ball body */}
      <circle
        cx="56"
        cy="32"
        r="18"
        fill="url(#km-ball)"
        stroke={brand}
        strokeWidth="0.8"
        opacity="0.99"
      />
      {/* center pentagon — iconic Telstar white panel */}
      <polygon
        points="56,21 63,26 60.5,34 51.5,34 49,26"
        fill="#f8f8fa"
        stroke={brand}
        strokeWidth="0.4"
        strokeLinejoin="round"
        opacity="0.92"
      />
      {/* surrounding hexagonal panels */}
      <polygon
        points="49,26 51.5,18 56,15 60.5,18 63,26"
        fill="none"
        stroke="#f8f8fa"
        strokeWidth="0.7"
        opacity="0.55"
        strokeLinejoin="round"
      />
      <polygon
        points="49,26 51.5,34 47,40 41,38 41,30"
        fill="none"
        stroke="#f8f8fa"
        strokeWidth="0.7"
        opacity="0.55"
        strokeLinejoin="round"
      />
      <polygon
        points="63,26 60.5,34 65,40 71,38 71,30"
        fill="none"
        stroke="#f8f8fa"
        strokeWidth="0.7"
        opacity="0.55"
        strokeLinejoin="round"
      />
      <polygon
        points="51.5,34 60.5,34 63,42 56,46 49,42"
        fill="none"
        stroke="#f8f8fa"
        strokeWidth="0.7"
        opacity="0.55"
        strokeLinejoin="round"
      />
      {/* crimson edge accent */}
      <path
        d="M 70 22 A 18 18 0 0 1 72 38"
        fill="none"
        stroke={brand}
        strokeWidth="1.2"
        opacity="0.85"
        strokeLinecap="round"
      />
      {/* specular highlight */}
      <ellipse
        cx="49"
        cy="24"
        rx="3.5"
        ry="2"
        fill="#ffffff"
        opacity="0.40"
        transform="rotate(-25 49 24)"
      />
    </svg>
  );
};

// ---------------------------------------------------------------------------
// OrgDropdownItem — one row inside the popover
// ---------------------------------------------------------------------------

interface OrgDropdownItemProps {
  org: OrgMembership;
  isActive: boolean;
  switching: boolean;
  onSelect: (orgId: string) => void;
}

function OrgDropdownItem({ org, isActive, switching, onSelect }: OrgDropdownItemProps) {
  const [hovered, setHovered] = useState(false);
  const disabled = isActive || switching;
  const accentColor = orgAccentColor(org.displayName);
  const initial = org.displayName.trim().charAt(0).toUpperCase();

  const roleLabel = org.roles
    .slice(0, 2)
    .map((r) => r.charAt(0).toUpperCase() + r.slice(1))
    .join(' · ');

  const rowStyle: CSSProperties = {
    ...buttonReset(),
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    width: '100%',
    cursor: disabled ? 'default' : 'pointer',
    backgroundColor: hovered && !disabled ? theme.colors.bg.tertiary : theme.colors.bg.secondary,
    transition: theme.transitions.fast,
    textAlign: 'left',
  };

  const logoStyle: CSSProperties = {
    width: 30,
    height: 30,
    borderRadius: '6px',
    backgroundColor: alpha(accentColor, 0.13),
    border: `1px solid ${alpha(accentColor, 0.27)}`,
    color: accentColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700,
    flexShrink: 0,
  };

  const nameStyle: CSSProperties = {
    flex: 1,
    minWidth: 0,
  };

  const nameTextStyle: CSSProperties = {
    fontSize: '13px',
    fontWeight: isActive ? 600 : 500,
    color: isActive ? theme.colors.text.primary : theme.colors.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  };

  const roleTextStyle: CSSProperties = {
    fontSize: '11px',
    color: theme.colors.text.muted,
    fontFamily: 'var(--k-font-mono)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.07em',
    marginTop: '1px',
    whiteSpace: 'nowrap' as const,
    display: 'block',
  };

  return (
    <button
      type="button"
      role="menuitem"
      style={rowStyle}
      disabled={disabled}
      onClick={() => !disabled && onSelect(org.orgId)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-pressed={isActive}
    >
      {org.logoUrl ? (
        <img
          src={org.logoUrl}
          alt={org.displayName}
          style={{
            width: 30,
            height: 30,
            borderRadius: '6px',
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
      ) : (
        <div style={logoStyle} aria-hidden="true">
          {initial}
        </div>
      )}
      <div style={nameStyle}>
        <span style={nameTextStyle}>{org.displayName}</span>
        {roleLabel ? <span style={roleTextStyle}>{roleLabel}</span> : null}
      </div>
      {isActive && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <IconCheck />
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// TopBar component
// ---------------------------------------------------------------------------

export function TopBar() {
  const { user, orgs, activeOrg, switchOrg, switchingOrgName } = useAuth();
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  // "Compact" = narrow desktop / tablet band (768–1024). Without this the bar
  // tries to render every full-size element in one non-wrapping row and they
  // overflow/clip (orphaned chevron bug). In compact mode we shed the lowest-
  // value, redundant chrome (the role·name pill duplicates the avatar's role
  // line) and tighten the search so what remains fits cleanly.
  const compact = isTablet;
  const { t } = useTranslationTyped();
  const { toast } = useToast();

  const searchRef = useRef<HTMLInputElement>(null);
  const orgPopoverRef = useRef<HTMLDivElement>(null);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgSwitching, setOrgSwitching] = useState(false);

  // Profile view switcher (Coach / Parent / Kantera Admin) — moved here from the
  // sidebar. Shared via context so the sidebar nav re-filters when it changes.
  const { activeView, availableViews, setView } = useProfileViewContext();
  const viewPopoverRef = useRef<HTMLDivElement>(null);
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);

  // Teams for the club pill's team picker. Cheap list; the pill lets a coach
  // jump straight to any of their teams. Skipped on mobile — the club pill is
  // not rendered there, so the request would be pure waste.
  const { data: teamsData } = useApi<TeamListResponse>('/api/teams?limit=50', {
    skip: isMobile,
  });
  const teams = teamsData?.data ?? [];
  const hasTeams = teams.length > 0;

  const hasMultipleOrgs = orgs.length > 1;
  // The club pill opens if there is anything to choose: multiple orgs, or teams
  // to jump to.
  const clubPillInteractive = hasMultipleOrgs || hasTeams;

  // The view pill is a real switcher only when the user wears more than one hat.
  const canSwitchView = availableViews.length > 1;

  // ⌘K / Ctrl+K global shortcut — focus input and navigate to /search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        navigate('/search');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Close org dropdown on outside click
  useEffect(() => {
    if (!orgDropdownOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (orgPopoverRef.current && !orgPopoverRef.current.contains(e.target as Node)) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [orgDropdownOpen]);

  // Close org dropdown on Escape
  useEffect(() => {
    if (!orgDropdownOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOrgDropdownOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [orgDropdownOpen]);

  // Crossing a responsive breakpoint remounts the pills; close any open
  // dropdown so it can't reappear stuck-open when the band changes back.
  useEffect(() => {
    setOrgDropdownOpen(false);
    setViewDropdownOpen(false);
  }, [isMobile, compact]);

  // Close the view-switcher dropdown on outside click / Escape.
  useEffect(() => {
    if (!viewDropdownOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (viewPopoverRef.current && !viewPopoverRef.current.contains(e.target as Node)) {
        setViewDropdownOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setViewDropdownOpen(false);
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [viewDropdownOpen]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const q = searchRef.current?.value.trim() ?? '';
        navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
      }
    },
    [navigate],
  );

  const handleBell = useCallback(() => {
    navigate('/notifications');
  }, [navigate]);

  const handleMobileSearch = useCallback(() => {
    navigate('/search');
  }, [navigate]);

  const handleOrgToggle = useCallback(() => {
    if (clubPillInteractive && !orgSwitching) {
      setOrgDropdownOpen((prev) => !prev);
    }
  }, [clubPillInteractive, orgSwitching]);

  const handleTeamSelect = useCallback(
    (teamId: string) => {
      setOrgDropdownOpen(false);
      navigate(`/teams/${teamId}`);
    },
    [navigate],
  );

  const handleOrgSelect = useCallback(
    async (orgId: string) => {
      if (orgId === activeOrg?.orgId) return;
      setOrgDropdownOpen(false);
      setOrgSwitching(true);
      try {
        await switchOrg(orgId);
      } catch (err) {
        // A refused switch used to be an unhandled rejection: the popover closed and nothing said
        // why. A refusal the web knows by its code is worded in the person's language; anything
        // else shows the server's own message (KAN-172).
        toast(switchOrgFailureMessage(err, t), 'error');
      } finally {
        setOrgSwitching(false);
      }
    },
    [activeOrg, switchOrg, toast, t],
  );

  // Derived values
  //
  // ONE resolver, from ONE precedence: profile.displayName -> first + last ->
  // legacy user_metadata.name -> email local part. This file previously had TWO
  // different fallbacks — `user?.name || user?.email` here, and
  // `user?.name?.trim() ? user.name : ''` at the UserMenu call site — neither of
  // which looked at the profile row that the Settings form actually writes. So a
  // user with a filled-in first and last name saw "Set your name".
  //
  // resolveUserDisplayName returns null (never a prompt) when nothing is known,
  // so the "Set your name" copy can only render when it is actually true.
  const resolvedName = resolveUserDisplayName({
    profile,
    metadataName: user?.name,
    email: user?.email,
  });
  const userName = resolvedName ?? undefined;
  // Prefer the user's JWT role; fall back to the active org's role membership
  // (some accounts carry the role only on the org membership, not user.roles —
  // without this the avatar role subtitle renders blank). Matches the mockup's
  // two-line "NAME / ROLE" account cluster.
  const primaryRole = user?.roles?.[0] ?? activeOrg?.roles?.[0];
  const userRoleLabel = formatRoleLabel(primaryRole);
  const initials =
    resolveUserInitials({ profile, metadataName: user?.name, email: user?.email }) ||
    getInitials(userName);

  // Sort orgs: active first, then alphabetical
  const sortedOrgs = [...(orgs ?? [])].sort((a, b) => {
    if (a.isActive && !b.isActive) return -1;
    if (!a.isActive && b.isActive) return 1;
    return a.displayName.localeCompare(b.displayName);
  });

  // ---------------------------------------------------------------------------
  // Styles — inline CSSProperties only, no Tailwind
  // ---------------------------------------------------------------------------

  const containerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: '20px',
    padding: '0 4px',
    height: '100%',
  };

  // LEFT cluster
  const leftStyle: CSSProperties = css(flexRow('12px'), {
    alignItems: 'center',
    flexShrink: 0,
  }) as CSSProperties;

  const wordmarkWrapStyle: CSSProperties = {
    lineHeight: 1.2,
  };

  const wordmarkStyle: CSSProperties = {
    fontSize: '16px',
    fontWeight: 800,
    letterSpacing: '0.13em',
    color: 'var(--k-text-primary)',
    textTransform: 'uppercase',
    fontFamily: 'var(--k-font-display)',
    lineHeight: 1,
    display: 'block',
  };

  const taglineStyle: CSSProperties = {
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--k-brand)',
    fontFamily: 'var(--k-font-mono)',
    display: 'block',
    marginTop: '3px',
    lineHeight: 1,
  };

  // CENTER search
  const searchWrapStyle: CSSProperties = {
    flex: 1,
    // Narrower search in the compact band frees room for the org pill + avatar
    // so the right cluster never gets crushed/clipped.
    maxWidth: compact ? '18rem' : '36rem',
    minWidth: 0,
    position: 'relative',
    display: isMobile ? 'none' : 'block',
  };

  const searchIconStyle: CSSProperties = {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--k-text-muted)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
  };

  const searchInputStyle: CSSProperties = {
    width: '100%',
    background: 'var(--k-bg-input)',
    border: '1px solid var(--k-border)',
    borderRadius: '9999px',
    paddingLeft: '40px',
    paddingRight: '60px',
    paddingTop: '10px',
    paddingBottom: '10px',
    fontSize: '13px',
    color: 'var(--k-text-primary)',
    fontFamily: 'var(--k-font-body)',
    outline: 'none',
    transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
  };

  const kbdStyle: CSSProperties = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '10px',
    color: 'var(--k-text-muted)',
    fontFamily: 'var(--k-font-mono)',
    border: '1px solid var(--k-border)',
    borderRadius: '4px',
    padding: '2px 6px',
    background: 'var(--k-bg-elevated)',
    lineHeight: 1.5,
    pointerEvents: 'none',
    userSelect: 'none',
  };

  // RIGHT cluster
  const rightStyle: CSSProperties = css(flexRow('8px'), {
    alignItems: 'center',
    flexShrink: 0,
  }) as CSSProperties;

  // Org pill — matches v2 mockup's account-cluster selectors: neutral dark
  // surface, hairline border, secondary text + chevron (NOT brand-outlined).
  const orgPillStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '7px 12px',
    borderRadius: '8px',
    background: 'var(--k-bg-elevated)',
    border: '1px solid var(--k-border)',
    fontSize: '12px',
    fontWeight: 500,
    fontFamily: 'var(--k-font-body)',
    letterSpacing: '0.01em',
    color: 'var(--k-text-secondary)',
    whiteSpace: 'nowrap',
    cursor: hasMultipleOrgs && !orgSwitching ? 'pointer' : 'default',
    outline: 'none',
    transition: 'background 0.15s ease, border-color 0.15s ease',
  };

  // Role · Name display pill — static, no dropdown
  const rolePillStyle: CSSProperties = {
    display: isMobile ? 'none' : 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '5px 12px',
    borderRadius: '9999px',
    background: 'transparent',
    border: '1px solid var(--k-border)',
    fontSize: '11px',
    fontWeight: 500,
    fontFamily: 'var(--k-font-mono)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: 'var(--k-text-secondary)',
    whiteSpace: 'nowrap',
  };

  // Org dropdown popover
  const dropdownStyle: CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    width: '260px',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.9)',
    overflow: 'hidden',
    zIndex: 99999,
    background: theme.colors.bg.secondary,
    border: `1px solid ${theme.colors.border.primary}`,
  };

  // Bell button
  const bellButtonStyle: CSSProperties = {
    position: 'relative',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'transparent',
    border: 'none',
    color: 'var(--k-text-secondary)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.18s ease, color 0.18s ease',
    padding: 0,
  };

  // User cluster — the avatar + name + role is now the UserMenu account
  // dropdown (Wave A6 Task 2.4). Styles live inside that component.

  const switchingLabelStyle: CSSProperties = {
    fontSize: '11px',
    fontFamily: 'var(--k-font-mono)',
    color: 'var(--k-text-muted)',
    fontStyle: 'italic',
  };

  return (
    <div style={containerStyle} data-topbar>
      {/* LEFT — logo mark + wordmark + tagline */}
      <div style={leftStyle}>
        <KanteraMark />
        <div style={wordmarkWrapStyle}>
          <span style={wordmarkStyle}>Kantera</span>
          <span style={taglineStyle}>Soccer Intelligence</span>
        </div>
      </div>

      {/* CENTER — search input */}
      <div style={searchWrapStyle}>
        <label htmlFor="topbar-search" style={{ position: 'absolute', left: '-9999px' }}>
          {t('webTopBar.searchLabel')}
        </label>
        <div style={searchIconStyle}>
          <IconSearch />
        </div>
        <input
          id="topbar-search"
          ref={searchRef}
          type="search"
          style={searchInputStyle}
          placeholder={t('webTopBar.searchPlaceholder')}
          aria-label={t('webTopBar.searchAriaLabel')}
          onKeyDown={handleSearchKeyDown}
          onFocus={(e) => {
            e.currentTarget.style.border = '1px solid var(--k-accent)';
            e.currentTarget.style.boxShadow = '0 0 0 3px var(--k-accent-glow)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.border = '1px solid var(--k-border)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
        <kbd style={kbdStyle}>⌘K</kbd>
      </div>

      {/* RIGHT — (mobile search) + org pill + role pill + bell + user */}
      <div style={rightStyle}>
        {/* Mobile-only search icon */}
        {isMobile && (
          <button
            style={bellButtonStyle}
            onClick={handleMobileSearch}
            aria-label={t('webTopBar.searchAriaLabel')}
            title={t('webTopBar.searchAriaLabel')}
          >
            <IconSearch />
          </button>
        )}

        {/* Org pill — hidden on mobile, shows "Switching…" during transition */}
        {!isMobile &&
          (switchingOrgName ? (
            <span style={switchingLabelStyle}>{t('webTopBar.switchingOrg')}</span>
          ) : (
            <div ref={orgPopoverRef} style={{ position: 'relative' }}>
              {/* Pill trigger — opens when there are orgs to switch OR teams to jump to */}
              <button
                type="button"
                style={orgPillStyle}
                onClick={handleOrgToggle}
                aria-haspopup={clubPillInteractive ? 'menu' : undefined}
                aria-expanded={clubPillInteractive ? orgDropdownOpen : undefined}
                aria-label={
                  clubPillInteractive
                    ? t('webTopBar.orgPillAriaLabelSwitchable', {
                        name: activeOrg?.displayName ?? '',
                      })
                    : t('webTopBar.orgPillAriaLabel', { name: activeOrg?.displayName ?? '' })
                }
                onMouseEnter={(e) => {
                  if (clubPillInteractive) {
                    (e.currentTarget as HTMLButtonElement).style.background = 'var(--k-bg-hover)';
                    (e.currentTarget as HTMLButtonElement).style.border =
                      '1px solid var(--k-border-strong, var(--k-border))';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'var(--k-bg-elevated)';
                  (e.currentTarget as HTMLButtonElement).style.border = '1px solid var(--k-border)';
                }}
              >
                {activeOrg?.displayName ?? ''}
                {clubPillInteractive && <IconChevronDown rotated={orgDropdownOpen} />}
              </button>

              {/* Dropdown — orgs (if more than one) + this club's teams */}
              {orgDropdownOpen && clubPillInteractive && (
                <div
                  style={dropdownStyle}
                  role="menu"
                  aria-label={t('webTopBar.orgDropdownAriaLabel')}
                >
                  <div style={css(flexCol('0px'), { paddingTop: '6px', paddingBottom: '6px' })}>
                    {hasMultipleOrgs &&
                      sortedOrgs.map((org) => (
                        <OrgDropdownItem
                          key={org.orgId}
                          org={org}
                          isActive={org.isActive}
                          switching={orgSwitching}
                          onSelect={handleOrgSelect}
                        />
                      ))}
                    {hasTeams && (
                      <>
                        <div
                          style={{
                            padding: '8px 14px 4px',
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: theme.colors.text.muted,
                            borderTop: hasMultipleOrgs
                              ? `1px solid ${theme.colors.border.primary}`
                              : undefined,
                            marginTop: hasMultipleOrgs ? '4px' : undefined,
                          }}
                        >
                          {t('webTopBar.teamsHeading')}
                        </div>
                        {teams.map((team) => (
                          <button
                            key={team.id}
                            type="button"
                            role="menuitem"
                            onClick={() => handleTeamSelect(team.id)}
                            style={css(buttonReset(), {
                              width: '100%',
                              textAlign: 'left',
                              padding: '8px 14px',
                              fontSize: '13px',
                              color: theme.colors.text.primary,
                              cursor: 'pointer',
                              background: 'transparent',
                            })}
                            onMouseEnter={(e) =>
                              ((e.currentTarget as HTMLButtonElement).style.background = alpha(
                                theme.colors.accent.primary,
                                0.08,
                              ))
                            }
                            onMouseLeave={(e) =>
                              ((e.currentTarget as HTMLButtonElement).style.background =
                                'transparent')
                            }
                          >
                            {team.name}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

        {/* Role pill → profile VIEW switcher (Kantera Admin / Coach / Parent).
            When the user wears more than one hat this is an interactive switcher
            and stays reachable on EVERY breakpoint — it moved here from the
            sidebar, so hiding it in the compact/mobile band would leave the app
            with no view switcher at all (iPad included). The "· name" suffix is
            dropped in the compact/mobile band to save room (the avatar already
            shows the name). With a single view it degrades to a static label
            that, like the old role pill, is shed in the compact/mobile band as
            redundant with the avatar's role line. */}
        {canSwitchView ? (
          <div ref={viewPopoverRef} style={{ position: 'relative' }}>
            <button
              type="button"
              style={css(rolePillStyle as CSSProperties, {
                display: 'inline-flex',
                cursor: 'pointer',
              })}
              onClick={() => setViewDropdownOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={viewDropdownOpen}
              aria-label={t('webProfileSwitcher.ariaLabel')}
            >
              {t(`webProfileSwitcher.${activeView.labelKey}`)}
              {!compact && !isMobile && userName ? ` · ${userName}` : ''}
              <IconChevronDown rotated={viewDropdownOpen} />
            </button>

            {viewDropdownOpen && (
              <div style={dropdownStyle} role="menu" aria-label={t('webProfileSwitcher.ariaLabel')}>
                <div style={css(flexCol('0px'), { paddingTop: '6px', paddingBottom: '6px' })}>
                  {availableViews.map((v) => {
                    const active = v.id === activeView.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setView(v.id);
                          setViewDropdownOpen(false);
                        }}
                        style={css(buttonReset(), flexRow('8px'), {
                          width: '100%',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '9px 14px',
                          fontSize: '13px',
                          fontWeight: active ? 700 : 500,
                          color: theme.colors.text.primary,
                          cursor: 'pointer',
                          background: 'transparent',
                        })}
                        onMouseEnter={(e) =>
                          ((e.currentTarget as HTMLButtonElement).style.background = alpha(
                            theme.colors.accent.primary,
                            0.08,
                          ))
                        }
                        onMouseLeave={(e) =>
                          ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')
                        }
                      >
                        {t(`webProfileSwitcher.${v.labelKey}`)}
                        {active ? <IconCheck /> : <span style={{ width: 14 }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          !isMobile &&
          !compact && (
            <div style={rolePillStyle}>
              {t(`webProfileSwitcher.${activeView.labelKey}`)}
              {userName ? ` · ${userName}` : ''}
            </div>
          )
        )}

        {/* Bell / notifications */}
        <button
          style={bellButtonStyle}
          onClick={handleBell}
          aria-label={t('webTopBar.notificationsAriaLabel')}
          title={t('webTopBar.notificationsAriaLabel')}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(244,245,246,0.06)';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--k-text-primary)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--k-text-secondary)';
          }}
        >
          <IconBell />
        </button>

        {/* User cluster — avatar account menu (Wave A6 Task 2.4) */}
        <UserMenu
          name={resolvedName ?? ''}
          email={user?.email ?? ''}
          roleLabel={userRoleLabel ?? ''}
          initials={initials}
          avatarUrl={profile?.avatarUrl ?? null}
          compact={isMobile}
        />
      </div>
    </div>
  );
}
